import { AnimatePresence, motion } from "framer-motion";
import { Lock, Gift } from "lucide-react";
import { ElfFigure } from "@/components/Elf";

const PALETTES = {
  red: "bg-gradient-to-br from-[#8B1E2B] to-[#5C111C] text-amber-100 border-amber-500/40",
  green: "bg-gradient-to-br from-[#14402D] to-[#0B2318] text-amber-200 border-emerald-400/25",
  cream: "bg-gradient-to-br from-[#FAF6EE] to-[#E7DCC3] text-[#14352A] border-amber-600/50",
  gold: "bg-[linear-gradient(135deg,#BF953F,#FCF6BA_25%,#B38728_50%,#FBF5B7_75%,#AA771C)] text-[#3D2B07] border-amber-200/80",
};

export const AdventDoor = ({
  day,
  isLocked,
  isDemo,
  isOpened,
  isOpening,
  reward,
  unlockDate,
  palette = "green",
  featured = false,
  tilt = 0,
  elfPop = false,
  onSelect,
}) => {
  const open = isOpened || isOpening;
  return (
    <div
      className={`relative z-0 transition-transform duration-300 hover:z-20 ${
        featured ? "col-span-2 sm:col-start-5 sm:row-start-4 sm:row-span-2" : ""
      }`}
      style={{ transform: `rotate(${tilt}deg)` }}
    >
      {/* elf pops out of the door */}
      <AnimatePresence>
        {elfPop && (
          <motion.div
            data-testid={`elf-pop-day-${day}`}
            className="pointer-events-none absolute -top-12 left-1/2 z-30 w-14 sm:-top-16 sm:w-20"
            initial={{ opacity: 0, scale: 0, x: "-50%", y: 34 }}
            animate={{ opacity: 1, scale: 1, x: "-50%", y: 0 }}
            exit={{ opacity: 0, scale: 0.5, x: "-50%", y: 20 }}
            transition={{ type: "spring", stiffness: 230, damping: 14, delay: 0.35 }}
          >
            <ElfFigure />
          </motion.div>
        )}
      </AnimatePresence>

      <button
        type="button"
        data-testid={`door-day-${day}`}
        data-demo={isDemo || undefined}
        data-locked={isLocked || undefined}
        data-unlock-date={unlockDate}
        aria-label={`Apri casella del giorno ${day}`}
        onClick={() => onSelect({ day, isLocked, isDemo, reward, unlockDate })}
        className={`door-scene group relative block w-full cursor-pointer rounded-xl outline-none transition-transform duration-300 hover:scale-[1.06] focus-visible:ring-2 focus-visible:ring-amber-300 ${
          featured ? "h-full" : "aspect-square"
        }`}
      >
        {/* golden light revealed behind the door */}
        <div
          className={`absolute inset-0 flex items-center justify-center overflow-hidden rounded-xl transition-opacity duration-500 ${
            open ? "opacity-100" : "opacity-0"
          } bg-[radial-gradient(circle_at_50%_45%,rgba(252,246,186,0.95),rgba(212,175,55,0.55)_48%,rgba(90,60,10,0.2)_78%)]`}
        >
          <Gift
            className={`${featured ? "h-10 w-10" : "h-5 w-5 sm:h-6 sm:w-6"} text-[#6B4E0C] drop-shadow-[0_2px_6px_rgba(255,240,180,0.8)]`}
          />
        </div>

        {/* the door itself */}
        <div
          className={`door-front absolute inset-0 flex items-center justify-center rounded-xl border ${
            PALETTES[palette]
          } ${open ? "door-front-open" : ""} ${
            isLocked ? "opacity-90 saturate-[.8]" : ""
          } ${
            featured
              ? "vigilia-glow border-2 !border-[#FCF6BA]"
              : "transition-[box-shadow,border-color] duration-300 group-hover:border-amber-300/90 group-hover:shadow-[0_0_18px_rgba(212,175,55,0.35),inset_0_0_16px_rgba(212,175,55,0.3)]"
          }`}
        >
          <span
            className={`font-display font-bold transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:scale-110 ${
              featured ? "text-5xl sm:text-7xl" : "text-xl sm:text-3xl"
            }`}
          >
            {day}
          </span>
          {isLocked && !featured && (
            <Lock className="absolute bottom-1.5 right-1.5 h-3 w-3 opacity-70 sm:h-4 sm:w-4" />
          )}
          {featured && (
            <Gift className="absolute right-2.5 top-2.5 h-5 w-5 text-amber-100 sm:h-6 sm:w-6" />
          )}
        </div>

        {/* star burst while opening */}
        {isOpening &&
          [...Array(6)].map((_, i) => (
            <motion.span
              key={i}
              className="pointer-events-none absolute left-1/2 top-1/2 h-1.5 w-1.5 rounded-full bg-amber-200"
              style={{ boxShadow: "0 0 8px rgba(252,246,186,0.9)" }}
              initial={{ opacity: 1, x: 0, y: 0, scale: 0.2 }}
              animate={{
                opacity: 0,
                x: Math.cos(i * 1.047) * 44,
                y: Math.sin(i * 1.047) * 44,
                scale: 1.5,
              }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            />
          ))}

        {featured && (
          <div
            data-testid="door-24-tooltip"
            className="pointer-events-none absolute -top-10 left-1/2 z-30 -translate-x-1/2 whitespace-nowrap rounded-full border border-amber-300/50 bg-[#0A1D15]/95 px-3 py-1 text-xs text-amber-200 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          >
            La sorpresa della Vigilia ✨
          </div>
        )}
      </button>
    </div>
  );
};
